import { Button, OutlinedInput, ClickAwayListener, Popper } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import React from "react";
import styles from "./ProcessFeeOptionSelect.module.scss";
import { ProcessFeeOption } from "../../apis/models/ProcessFeeDetailsResponse";
import { ProcessFeeOptionRequestItem } from "../../apis/models/ProcessFeeRequest";
import { formatNumber } from "../../libs/helpers";

const NewOptionInput: React.FC<{onAdd: (work: string, unitPrice: number) => void}> = (props) => {
  const [work, setWork] = React.useState<string>("");
  const [unitPrice, setUnitPrice] = React.useState<string>("");
  const disableButton = React.useMemo(() => {
    return !work || !unitPrice || isNaN(+unitPrice) || parseInt(unitPrice) < 1
  }, [work, unitPrice])

  const handleAdd = () => {
    if (disableButton) {
      return;
    }
    setWork("")
    setUnitPrice("")
    props.onAdd(work, parseInt(unitPrice))
  }

  return (
    <div className={styles.menu_footer}>
      <OutlinedInput className={styles.menu_footer_work} onChange={(e) => setWork(e.target.value)} value={work} />
      <OutlinedInput className={styles.menu_footer_price} onChange={(e) => setUnitPrice(e.target.value)} value={unitPrice} type="number"/>
      <AddCircleRoundedIcon
        style={{cursor: "pointer"}}
        color={disableButton ? "disabled" : "primary"}
        onClick={() => {handleAdd()}}
      />
    </div> 
  )
}

const ProcessFeeOptionSelect: React.FC<ProcessFeeOptionSelectProps> = (props: ProcessFeeOptionSelectProps) => {
  const [value, setValue] = React.useState<string>(props.defaultValue ? props.defaultValue : "")
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isTextFocus, setIsTextFocus] = React.useState<boolean>(false);
  const [isPopupHover, setIsPopupHover] = React.useState<boolean>(false);
  const [isAddingWork, setIsAddingWork] = React.useState<boolean>(false);
  const inputElement = React.useRef(null)

  React.useEffect(() => {
    setValue(props.defaultValue ? props.defaultValue : "");
  }, [props.defaultValue])

  React.useEffect(() => {
    if (anchorEl) {
      return;
    }
    setIsAddingWork(false);
  }, [anchorEl]);

  const suggestions = React.useMemo(() => {
    return props.options
      .filter(option => option.processFeeOption.workDetails.includes(value))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0 ));
  }, [props.options, value]);

  const handleSelectOption = (option: ProcessFeeOption) => {
    setValue(option.processFeeOption.workDetails);
    props.onSelect(option);
    setAnchorEl(null);
  }

  const handleAddOption = (work: string, unitPrice: number) => {
    const lastOrder = Math.max(...(props.options.map(option => option.order ?? 0)))
    props.onAddOption({
      workDetails: work,
      unitPrice: unitPrice,
      order: lastOrder + 1
    })
  }

  const onChangeText = (str: string) => {
    setValue(str);
    props.onSelect(undefined);
  }

  const onBlurText = () => {
    setIsTextFocus(false);
    if (!isPopupHover) {
      setAnchorEl(null);
    }
  }

  const closePopper = () => {
    if (!isTextFocus) {
      setAnchorEl(null)
    }
  }
  
  const togglePopup = () => {
    setAnchorEl(prev => {
      if (!prev) {
        return inputElement.current;
      } else {
        return null;
      }
    })
  }
  
  return (
    <div style={{width: "100%"}}>
      <OutlinedInput
        ref={inputElement}
        color="primary"
        endAdornment={<ArrowDropDownIcon color="primary" style={{cursor: "pointer"}} onClick={() => {togglePopup()}} />}
        value={value}
        onChange={(e) => onChangeText(e.target.value)}
        onFocus={(e) => {setIsTextFocus(true); setAnchorEl(e.currentTarget)}}
        onBlur={(e) => {onBlurText()}}
        sx={props.errorMessage ? {borderColor: "#EB4E4E"} : {}}
      />
      {props.errorMessage ? ( 
        <div className={styles.err_mess}>{props.errorMessage}</div>
      ) : (
        <></>
      )}
      {anchorEl !== null ? (
        <ClickAwayListener onClickAway={() => {closePopper()}}>
          <Popper
            anchorEl={anchorEl}
            open={true}
            disablePortal={true}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.15)",
              zIndex: "10"
            }}
          >
            <div onMouseOver={() => setIsPopupHover(true)} onMouseOut={() => setIsPopupHover(false)}>
              <div className={styles.menu_content}>
                {
                  suggestions.length > 0 ? suggestions.map((item, index) => (
                    <div className={styles.menu_content_details} key={index} onClick={() => handleSelectOption(item)}>
                      <span className={styles.menu_content_details_work}>{item.processFeeOption.workDetails}</span>
                      <span className={styles.menu_content_details_price}>{formatNumber(item.processFeeOption.unitPrice)}</span>
                    </div>
                  )) :
                  <>なし</>
                }
              </div>
              {
                isAddingWork ? (
                  <NewOptionInput onAdd={handleAddOption}/>
                ) : (
                  <div className={styles.menu_footer_button}>
                    <Button variant="outlined" color="primary" onClick={() => setIsAddingWork(true)}>新規追加</Button>
                  </div>
                )
              }
            </div>
          </Popper>
        </ClickAwayListener>
      ) : (
        <></>
      )}
    </div>
  )
}

export default ProcessFeeOptionSelect;

export type ProcessFeeOptionSelectProps = {
  defaultValue?: string;
  options: ProcessFeeOption[];
  onSelect: (value: ProcessFeeOption | undefined) => void;
  onAddOption: (option: ProcessFeeOptionRequestItem) => void;
  errorMessage?: string;
}